import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import * as XLSX from "xlsx";
import { Link, useLocation, useParams } from "react-router-dom";
import Layouts from "../../layouts/layouts";
import "./MISPage.css";
import MISSendEmailModal from "./MISSendEmailModal";
import InvoiceSendEmailModal from "./InvoiceSendEmailModal";
import MISInvoiceInbox from "./MISInvoiceInbox";

const API = `${process.env.REACT_APP_BASE_URL}/api/mis`;
const MONTH_RE = /^\d{4}-(0[1-9]|1[0-2])$/;

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */
const getTodayIST = () =>
  new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());

const formatMoney = (value) =>
  `₹${Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const formatNumber = (value) =>
  Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

// Starting/closing KM can legitimately be empty; don't show them as 0.00.
const formatNullableNumber = (value) =>
  value === null || value === undefined || value === ""
    ? null
    : formatNumber(value);

const formatDate = (value) =>
  value
    ? new Date(value).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        timeZone: "Asia/Kolkata",
      })
    : "-";

const formatDateTime = (value) =>
  value
    ? new Date(value).toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        timeZone: "Asia/Kolkata",
      })
    : "Not yet";

const getClientName = (client) =>
  client?.name || client?.clientName || client?.companyName || "Unknown Client";

const getId = (value) => value?._id || value || "";

const getVehicleName = (vehicle) =>
  vehicle?.numberplate ||
  vehicle?.vehicleNo ||
  vehicle?.registrationNumber ||
  vehicle?._id ||
  "N/A";

const getDriverName = (driver) =>
  driver?.name || driver?._id || driver || "N/A";

const authHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const getHeaders = () => ({
  "Content-Type": "application/json",
  ...authHeaders(),
});

const STATUS_STEPS = [
  { field: "misSent", at: "misSentAt", label: "MIS sent" },
  { field: "misApproved", at: "misApprovedAt", label: "MIS approved" },
  {
    field: "invoiceGenerated",
    at: "invoiceGeneratedAt",
    label: "Invoice generated",
  },
  { field: "invoiceSent", at: "invoiceSentAt", label: "Invoice sent" },
];

const RECONCILIATION_FIELD_LABELS = {
  payout: "Payout",
  ot: "OT Hours",
  otHours: "OT Hours",
  extraKm: "Extra KM",
  toll: "Toll",
  parking: "Parking",
  mcd: "MCD",
  startingKm: "Starting KM",
  closingKm: "Ending KM",
  totalKm: "Total KM",
};

const reconciliationStatusText = {
  matched: "Matched",
  mismatch: "Mismatch",
  "missing-in-mis": "Missing in MIS",
  "missing-in-excel": "Missing in Excel",
  duplicate: "Duplicate match",
  "not-provided": "Not in Excel",
};

const reconciliationBadge = (status) =>
  ({
    matched: "mis-badge--success",
    mismatch: "mis-badge--danger",
    "missing-in-mis": "mis-badge--warning",
    "missing-in-excel": "mis-badge--warning",
    duplicate: "mis-badge--danger",
    "not-provided": "mis-badge--neutral",
  })[status] || "mis-badge--neutral";

const showReconciliationValue = (value) =>
  value === null || value === undefined || value === ""
    ? "—"
    : typeof value === "number"
      ? value.toLocaleString("en-IN", { maximumFractionDigits: 2 })
      : String(value);

const RECON_FILTERS = [
  { key: "issues", label: "Needs attention" },
  { key: "all", label: "All" },
  { key: "mismatch", label: "Mismatch" },
  { key: "missing-in-mis", label: "Missing in MIS" },
  { key: "missing-in-excel", label: "Missing in Excel" },
  { key: "duplicate", label: "Duplicates" },
  { key: "matched", label: "Matched" },
];

const SIGNATURE_STORAGE_KEY = "invoiceSignatureImage";

const getSavedSignature = () => {
  try {
    return localStorage.getItem(SIGNATURE_STORAGE_KEY) || "";
  } catch {
    return "";
  }
};

// Shrinks the uploaded signature so it stays light in storage and in the PDF request.
const prepareSignatureImage = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Could not read the image file."));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error("This file is not a valid image."));
      img.onload = () => {
        const scale = Math.min(1, 500 / img.width, 200 / img.height);
        const canvas = document.createElement("canvas");
        canvas.width = Math.max(1, Math.round(img.width * scale));
        canvas.height = Math.max(1, Math.round(img.height * scale));
        canvas
          .getContext("2d")
          .drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL("image/png"));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });

// With responseType "blob", server errors arrive as a Blob; read the real message out of it.
const getBlobErrorMessage = async (err, fallback) => {
  try {
    const data = err.response?.data;
    if (data instanceof Blob) {
      const parsed = JSON.parse(await data.text());
      return parsed.message || fallback;
    }
    return data?.message || fallback;
  } catch {
    return fallback;
  }
};

const INVOICE_EDIT_FIELDS = [
  {
    key: "clientName",
    label: "Client / buyer name",
    placeholder: "Leave blank to use the saved client name",
  },
  {
    key: "clientAddress",
    label: "Client address",
    placeholder: "Leave blank to use the saved address",
    rows: 3,
  },
  {
    key: "clientGSTIN",
    label: "Client GSTIN",
    placeholder: "Leave blank to use the saved GSTIN",
  },
  {
    key: "placeOfSupply",
    label: "Place of supply",
    placeholder: "Leave blank to use the saved state",
  },
  {
    key: "serviceDescription",
    label: "Service description",
    placeholder: "Default service description",
  },
  {
    key: "invoiceDescription",
    label: "Invoice / MIS description",
    placeholder: "Default month, trips and KM text",
    rows: 2,
  },
  {
    key: "referenceText",
    label: "Reference text",
    placeholder: "Default MIS reference",
  },
  {
    key: "deliveryTerms",
    label: "Delivery terms",
    placeholder: "Optional delivery terms",
  },
];



/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */
export default function MISPage() {
  const { clientId } = useParams();
  const location = useLocation();

  const initialMonth = (() => {
    const q = new URLSearchParams(location.search).get("month");
    return q && MONTH_RE.test(q) ? q : getTodayIST().slice(0, 7);
  })();

  const [date, setDate] = useState(getTodayIST());
  const [month, setMonth] = useState(initialMonth);
  const [generating, setGenerating] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [clients, setClients] = useState([]);
  const [clientSearch, setClientSearch] = useState("");
  const [details, setDetails] = useState([]);
  const [detailDateFrom, setDetailDateFrom] = useState("");
  const [detailDateTo, setDetailDateTo] = useState("");
  const [detailPage, setDetailPage] = useState(1);
  const [detailPageSize, setDetailPageSize] = useState(10);
  const [summaryPage, setSummaryPage] = useState(1);
  const [summaryPageSize, setSummaryPageSize] = useState(10);
  const [clientName, setClientName] = useState("");
  const [misTimeline, setMisTimeline] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [activeCell, setActiveCell] = useState(null);
  const [cellValue, setCellValue] = useState("");
  const [savingCell, setSavingCell] = useState(null);
  const [savingVehicleStatus, setSavingVehicleStatus] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [showExportOptions, setShowExportOptions] = useState(false);
  const [savingStatus, setSavingStatus] = useState(null);
  const [generatingInvoiceFor, setGeneratingInvoiceFor] = useState(null);
  const [showSendMIS, setShowSendMIS] = useState(false);
  const [showSendInvoice, setShowSendInvoice] = useState(false);
  const [inboxPurpose, setInboxPurpose] = useState(null);

  const [reconciliationFile, setReconciliationFile] = useState(null);
  const [fileInputKey, setFileInputKey] = useState(0);
  const [reconciliationLoading, setReconciliationLoading] = useState(false);
  const [reconciliationApplying, setReconciliationApplying] = useState(false);
  const [reconciliationError, setReconciliationError] = useState("");
  const [reconciliationReport, setReconciliationReport] = useState(null);
  const [reconciliationSelected, setReconciliationSelected] = useState({});
  const [showReconciliationModal, setShowReconciliationModal] = useState(false);
  const [reconFilter, setReconFilter] = useState("issues");

  const [confirmState, setConfirmState] = useState(null);

  const [invoicePreview, setInvoicePreview] = useState(null);
  const [invoicePdfEdits, setInvoicePdfEdits] = useState({});
  const [invoicePdfUrl, setInvoicePdfUrl] = useState("");
  const [invoicePdfLoading, setInvoicePdfLoading] = useState(false);
  const [invoicePdfError, setInvoicePdfError] = useState("");
  const [invoiceDirty, setInvoiceDirty] = useState(false);
  const [showInvoiceNumberModal, setShowInvoiceNumberModal] = useState(false);
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [invoiceNumberLoading, setInvoiceNumberLoading] = useState(false);
  const [invoiceNumberError, setInvoiceNumberError] = useState("");
  const [pendingInvoiceClient, setPendingInvoiceClient] = useState(null);
  const previewReqRef = useRef(0); // latest preview request wins
  const pdfUrlRef = useRef("");

  const requestRef = useRef(0); // ignores responses from outdated requests
  const cancelEditRef = useRef(false); // Escape should not save on blur

  /* ---------- Confirm dialog (replaces window.confirm) ---------- */
  const askConfirm = useCallback(
    (options) =>
      new Promise((resolve) => setConfirmState({ ...options, resolve })),
    [],
  );

  const closeConfirm = (result) => {
    confirmState?.resolve(result);
    setConfirmState(null);
  };

  /* ---------- Global side effects ---------- */
  // Success messages dismiss themselves.
  useEffect(() => {
    if (!successMessage) return undefined;
    const timer = setTimeout(() => setSuccessMessage(""), 5000);
    return () => clearTimeout(timer);
  }, [successMessage]);

  // Escape closes the top-most overlay; body doesn't scroll behind overlays.
  useEffect(() => {
    const onKey = (event) => {
      if (event.key !== "Escape") return;
      if (confirmState) {
        confirmState.resolve(false);
        setConfirmState(null);
      } else if (invoicePreview) {
        if (!invoicePdfLoading) closeInvoicePreview();
      } else if (showReconciliationModal && !reconciliationApplying) {
        setShowReconciliationModal(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    confirmState,
    invoicePreview,
    invoicePdfLoading,
    showReconciliationModal,
    reconciliationApplying,
  ]);

  // Release the PDF blob URL if the page unmounts while a preview is open.
  useEffect(() => {
    pdfUrlRef.current = invoicePdfUrl;
  }, [invoicePdfUrl]);
  useEffect(
    () => () => {
      if (pdfUrlRef.current) window.URL.revokeObjectURL(pdfUrlRef.current);
    },
    [],
  );

  useEffect(() => {
    document.body.style.overflow =
      showReconciliationModal || confirmState || invoicePreview ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [showReconciliationModal, confirmState, invoicePreview]);

  /* ---------- Data loading ---------- */
  const fetchClientSummary = useCallback(async () => {
    if (!MONTH_RE.test(month)) return;
    const reqId = ++requestRef.current;
    setLoading(true);
    setError("");
    try {
      const response = await axios.get(`${API}/get/summary`, {
        params: { month },
        headers: getHeaders(),
      });
      if (reqId !== requestRef.current) return;
      setClients(response.data.records || []);

    } catch (err) {
      if (reqId !== requestRef.current) return;
      console.error("MIS summary fetch error:", err);
      setError(
        err.response?.data?.message || "Unable to load the client MIS summary.",
      );
      setClients([]);
    } finally {
      if (reqId === requestRef.current) setLoading(false);
    }
  }, [month]);

  const fetchClientDetails = useCallback(async () => {
    if (!clientId || !MONTH_RE.test(month)) return;
    const reqId = ++requestRef.current;
    setLoading(true);
    setError("");
    try {
      const response = await axios.get(`${API}/get`, {
        params: { clientId, month, page: 1, limit: 100 },
        headers: getHeaders(),
      });
      if (reqId !== requestRef.current) return;

      const parents = response.data.records || [];
      const parent =
        parents.find(
          (item) => String(getId(item.clientId)) === String(clientId),
        ) || parents[0];
      if (!parent) {
        setDetails([]);
        setMisTimeline(null);
        setClientName("Client MIS details");
        return;
      }

      setMisTimeline({
        misSent: Boolean(parent.misSent),
        misSentAt: parent.misSentAt ?? null,
        misApproved: Boolean(parent.misApproved),
        misApprovedAt: parent.misApprovedAt ?? null,
        invoiceGenerated: Boolean(parent.invoiceGenerated),
        invoiceGeneratedAt: parent.invoiceGeneratedAt ?? null,
        invoiceSent: Boolean(parent.invoiceSent),
        invoiceSentAt: parent.invoiceSentAt ?? null,
      });
      setClientName(getClientName(parent.clientId));
      const vehicleRows = (parent.vehicleRecords || []).map((record) => ({
        ...record,
        _misId: parent._id,
        _clientId: getId(parent.clientId),
        _month: parent.month,
      }));
      // Included entries first; excluded entries grouped at the bottom.
      vehicleRows.sort((a, b) => {
        const aExcluded = a.status === "exclude" ? 1 : 0;
        const bExcluded = b.status === "exclude" ? 1 : 0;
        if (aExcluded !== bExcluded) return aExcluded - bExcluded;
        return new Date(b.date || 0) - new Date(a.date || 0);
      });
      setDetails(vehicleRows);
    } catch (err) {
      if (reqId !== requestRef.current) return;
      console.error("MIS detail fetch error:", err);
      setError(err.response?.data?.message || "Unable to load MIS details.");
      setDetails([]);
    } finally {
      if (reqId === requestRef.current) setLoading(false);
    }
  }, [clientId, month]);

  useEffect(() => {
    setActiveCell(null);
    if (clientId) fetchClientDetails();
    else fetchClientSummary();
  }, [clientId, fetchClientDetails, fetchClientSummary]);

  /* ---------- Derived data ---------- */
  const filteredDetails = useMemo(() => {
    return details.filter((item) => {
      const parsedDate = item.date ? new Date(item.date) : null;
      const itemDate =
        parsedDate && !Number.isNaN(parsedDate.getTime())
          ? parsedDate.toISOString().slice(0, 10)
          : "";
      if (detailDateFrom && (!itemDate || itemDate < detailDateFrom)) return false;
      if (detailDateTo && (!itemDate || itemDate > detailDateTo)) return false;
      return true;
    });
  }, [details, detailDateFrom, detailDateTo]);

  const detailPageCount = Math.max(1, Math.ceil(filteredDetails.length / detailPageSize));
  const paginatedDetails = useMemo(() => {
    const start = (detailPage - 1) * detailPageSize;
    return filteredDetails.slice(start, start + detailPageSize);
  }, [filteredDetails, detailPage, detailPageSize]);

  useEffect(() => {
    setDetailPage(1);
  }, [detailDateFrom, detailDateTo, detailPageSize, clientId, month]);

  useEffect(() => {
    setDetailPage((current) => Math.min(current, detailPageCount));
  }, [detailPageCount]);

  const detailTotals = useMemo(
    () =>
      filteredDetails
        .filter((item) => item.status !== "exclude")
        .reduce(
          (t, item) => ({
            trips: t.trips + 1,
            totalKm: t.totalKm + (Number(item.totalKm) || 0),
            payout: t.payout + (Number(item.payout) || 0),
            toll: t.toll + (Number(item.toll) || 0),
            parking: t.parking + (Number(item.parking) || 0),
            mcd: t.mcd + (Number(item.mcd) || 0),
            ot: t.ot + (Number(item.ot) || 0),
            otAmount: t.otAmount + (Number(item.otAmount) || 0),
            extraKm: t.extraKm + (Number(item.extraKm) || 0),
            extraKmAmount: t.extraKmAmount + (Number(item.extraKmAmount) || 0),
          }),
          {
            trips: 0,
            totalKm: 0,
            payout: 0,
            toll: 0,
            parking: 0,
            mcd: 0,
            ot: 0,
            otAmount: 0,
            extraKm: 0,
            extraKmAmount: 0,
          },
        ),
    [filteredDetails],
  );
  const excludedCount = filteredDetails.length - detailTotals.trips;

  const filteredClients = useMemo(() => {
    const q = clientSearch.trim().toLowerCase();
    if (!q) return clients;
    return clients.filter((c) =>
      (c.clientName || getClientName(c.clientId)).toLowerCase().includes(q),
    );
  }, [clients, clientSearch]);

  const summaryPageCount = Math.max(1, Math.ceil(filteredClients.length / summaryPageSize));
  const paginatedClients = useMemo(() => {
    const start = (summaryPage - 1) * summaryPageSize;
    return filteredClients.slice(start, start + summaryPageSize);
  }, [filteredClients, summaryPage, summaryPageSize]);

  useEffect(() => {
    setSummaryPage((current) => Math.min(current, summaryPageCount));
  }, [summaryPageCount]);

  const summaryTotals = useMemo(
    () =>
      filteredClients.reduce(
        (t, c) => ({
          entries: t.entries + (Number(c.totalEntries) || 0),
          km: t.km + (Number(c.totalKm) || 0),
          payout: t.payout + (Number(c.totalPayout) || 0),
          toll: t.toll + (Number(c.totalToll) || 0),
          parking: t.parking + (Number(c.totalParking) || 0),
          mcd: t.mcd + (Number(c.totalMcd) || 0),
          ot: t.ot + (Number(c.totalOT) || 0),
          otAmount: t.otAmount + (Number(c.totalOTAmount) || 0),
          extraKm: t.extraKm + (Number(c.totalExtraKm) || 0),
          extraKmAmount: t.extraKmAmount + (Number(c.totalExtraKmAmount) || 0),
          approved: t.approved + (c.misApproved ? 1 : 0),
          invoiced: t.invoiced + (c.invoiceGenerated ? 1 : 0),
        }),
        {
          entries: 0,
          km: 0,
          payout: 0,
          toll: 0,
          parking: 0,
          mcd: 0,
          ot: 0,
          otAmount: 0,
          extraKm: 0,
          extraKmAmount: 0,
          approved: 0,
          invoiced: 0,
        },
      ),
    [filteredClients],
  );

  const reconciliationRows = useMemo(
    () =>
      reconciliationReport
        ? [
            ...(reconciliationReport.results || []),
            ...(reconciliationReport.missingInExcel || []).map((row) => ({
              ...row,
              status: row.status || "missing-in-excel",
              fields: [],
              message: "This MIS record was not found in the uploaded Excel.",
            })),
          ]
        : [],
    [reconciliationReport],
  );

  const reconFilterCounts = useMemo(() => {
    const counts = { all: reconciliationRows.length, issues: 0 };
    reconciliationRows.forEach((row) => {
      counts[row.status] = (counts[row.status] || 0) + 1;
      if (row.status !== "matched") counts.issues += 1;
    });
    return counts;
  }, [reconciliationRows]);

  const visibleReconRows = useMemo(
    () =>
      reconciliationRows.filter((row) =>
        reconFilter === "all"
          ? true
          : reconFilter === "issues"
            ? row.status !== "matched"
            : row.status === reconFilter,
      ),
    [reconciliationRows, reconFilter],
  );

  const selectedCount = Object.keys(reconciliationSelected).length;

  /* ---------- Actions: daily MIS ---------- */
  const handleGenerateMIS = async () => {
    if (!date) {
      setError("Select a date to generate MIS.");
      return;
    }
    const isFuture = date > getTodayIST();
    const ok = await askConfirm({
      title: "Generate daily MIS?",
      message: isFuture
        ? `${date} is in the future. Generate MIS anyway?`
        : `MIS will be generated for ${formatDate(date)}.`,
      confirmLabel: "Generate MIS",
    });
    if (!ok) return;

    setGenerating(true);
    setError("");
    setSuccessMessage("");
    try {
      const response = await axios.post(
        `${API}/generate`,
        { date },
        { headers: getHeaders() },
      );
      setSuccessMessage(
        response.data.message || `MIS generated for ${formatDate(date)}.`,
      );
      const targetMonth = date.slice(0, 7);
      if (targetMonth !== month)
        setMonth(targetMonth); // effect refetches
      else if (clientId) await fetchClientDetails();
      else await fetchClientSummary();
    } catch (err) {
      console.error("Generate MIS error:", err);
      setError(err.response?.data?.message || "Failed to generate MIS.");
    } finally {
      setGenerating(false);
    }
  };

  /* ---------- Actions: workflow status ---------- */

  const updateStatus = async (client, field, checked) => {
    if (field === "invoiceGenerated" && checked) {
      await openInvoiceNumberModal(client);
      return;
    }
    const misId = client.misId || client._id;

    if (!misId) {
      setError("Monthly MIS ID is missing. Refresh the page and try again.");
      return;
    }

    const step = STATUS_STEPS.find((s) => s.field === field);

    if (!checked) {
      const ok = await askConfirm({
        title: `Undo “${step?.label}”?`,
        message: `This marks ${
          client.clientName || "this client"
        } as not yet ${step?.label.toLowerCase()} and clears its timestamp.`,
        confirmLabel: "Undo status",
        danger: true,
      });

      if (!ok) return;
    }

    setSavingStatus(`${misId}:${field}`);
    setError("");
    setSuccessMessage("");

    try {
      // Generate the invoice before marking the status as generated.
      if (field === "invoiceGenerated" && checked) {
        const clientObjectId = getId(client.clientId);
        const selectedMonth = client.month || month;

        if (!clientObjectId || !MONTH_RE.test(selectedMonth)) {
          throw new Error(
            "A valid client and month are required to generate the invoice.",
          );
        }

        await axios.post(
          `${API}/invoice/generate`,
          {
            clientId: clientObjectId,
            month: selectedMonth,
          },
          { headers: getHeaders() },
        );
      }

      // Update MIS status. The backend should set or clear its timestamp.
      const response = await axios.patch(
        `${API}/${misId}/status`,
        { [field]: checked },
        { headers: getHeaders() },
      );

      const returnedMIS = response.data?.data || {};
      const timestampField = step?.at;

      setClients((current) =>
        current.map((item) =>
          String(item.misId || item._id) === String(misId)
            ? {
                ...item,
                [field]: checked,
                ...(timestampField
                  ? {
                      [timestampField]: returnedMIS[timestampField] ?? null,
                    }
                  : {}),
              }
            : item,
        ),
      );

      setSuccessMessage(
        field === "invoiceGenerated" && checked
          ? "Invoice generated and MIS timestamp updated."
          : response.data.message || "Status updated.",
      );

      // Refresh the latest status and timestamps from the backend.
      await fetchClientSummary();
    } catch (err) {
      console.error("MIS status update error:", err);

      setError(
        err.response?.data?.message ||
          err.message ||
          `Unable to update ${field}.`,
      );
    } finally {
      setSavingStatus(null);
    }
  };

  /* ---------- Actions: invoice ---------- */
  const loadInvoicePdfPreview = async (invoiceId, pdfEdits = {}) => {
    const reqId = ++previewReqRef.current;
    setInvoicePdfLoading(true);
    setInvoicePdfError("");
    try {
      const response = await axios.post(
        `${API}/invoice/${invoiceId}/pdf-preview`,
        { preview: true, pdfEdits },
        { headers: getHeaders(), responseType: "blob" },
      );
      if (reqId !== previewReqRef.current) return;
      const nextUrl = window.URL.createObjectURL(
        new Blob([response.data], { type: "application/pdf" }),
      );
      setInvoicePdfUrl((oldUrl) => {
        if (oldUrl) window.URL.revokeObjectURL(oldUrl);
        return nextUrl;
      });
      setInvoiceDirty(false);
    } catch (err) {
      const message = await getBlobErrorMessage(
        err,
        "Unable to create the invoice PDF preview. Check that the backend preview route is registered.",
      );
      if (reqId !== previewReqRef.current) return;
      setInvoicePdfError(message);
    } finally {
      if (reqId === previewReqRef.current) setInvoicePdfLoading(false);
    }
  };

  const openInvoiceNumberModal = async (client) => {
    const selectedMonth = client.month || month;
    const clientObjectId = getId(client.clientId);
    if (!clientObjectId || !MONTH_RE.test(selectedMonth)) {
      setError(
        "A valid client and month are required to generate the invoice.",
      );
      return;
    }
    setPendingInvoiceClient(client);
    setInvoiceNumber("");
    setInvoiceNumberError("");
    setShowInvoiceNumberModal(true);
    setInvoiceNumberLoading(true);
    try {
      const response = await axios.get(`${API}/invoice/number-suggestion`, {
        params: { clientId: clientObjectId, month: selectedMonth },
        headers: getHeaders(),
      });
      setInvoiceNumber(String(response.data?.invoiceNumber || ""));
    } catch (err) {
      setInvoiceNumberError(
        err.response?.data?.message ||
          "Could not load the suggested invoice number.",
      );
    } finally {
      setInvoiceNumberLoading(false);
    }
  };

  const handleGenerateInvoice = async (client, confirmedInvoiceNumber) => {
    const selectedMonth = client.month || month;
    const clientObjectId = getId(client.clientId);
    const clientLabel = client.clientName || getClientName(client.clientId);
    const misId = client.misId || client._id;

    if (!clientObjectId || !MONTH_RE.test(selectedMonth)) {
      setError(
        "A valid client and month are required to generate the invoice.",
      );
      return;
    }

    if (!misId) {
      setError("Monthly MIS ID is missing. Refresh the page and try again.");
      return;
    }

    if (!String(confirmedInvoiceNumber || "").trim()) {
      setError("Confirm an invoice number before generating the invoice.");
      return;
    }

    setGeneratingInvoiceFor(String(clientObjectId));
    setError("");
    setSuccessMessage("");

    try {
      // 1. Generate and save the invoice.
      const response = await axios.post(
        `${API}/invoice/generate`,
        {
          clientId: clientObjectId,
          month: selectedMonth,
          invoiceNumber: String(confirmedInvoiceNumber).trim(),
        },
        { headers: getHeaders() },
      );

      const invoiceId = response.data?.invoice?._id;

      if (!invoiceId) {
        throw new Error(
          "The server did not return an invoice ID after generating the invoice.",
        );
      }

      // 2. Update MIS status so the backend records invoiceGeneratedAt.
      const statusResponse = await axios.patch(
        `${API}/${misId}/status`,
        { invoiceGenerated: true },
        { headers: getHeaders() },
      );

      const returnedMIS = statusResponse.data?.data || {};
      const timestampField = "invoiceGeneratedAt";

      // Update the displayed status and timestamp immediately.
      setClients((current) =>
        current.map((item) =>
          String(item.misId || item._id) === String(misId)
            ? {
                ...item,
                invoiceGenerated: true,
                [timestampField]:
                  returnedMIS[timestampField] ?? item[timestampField] ?? null,
              }
            : item,
        ),
      );

      // 3. Prepare the PDF preview.
      const saved = getSavedSignature();
      const initialEdits = saved ? { signatureImage: saved } : {};

      setInvoicePreview({
        invoiceId,
        month: selectedMonth,
        clientId: clientObjectId,
        clientLabel,
      });

      setInvoicePdfEdits(initialEdits);
      setInvoicePdfError("");
      setInvoiceDirty(false);

      setSuccessMessage(
        `Invoice generated for ${clientLabel}. MIS status and timestamp updated.`,
      );

      // Refresh summary without blocking PDF preview.
      fetchClientSummary();

      await loadInvoicePdfPreview(invoiceId, initialEdits);
    } catch (err) {
      console.error("Generate invoice error:", err);

      setError(
        err.response?.data?.message ||
          err.message ||
          "Failed to generate invoice.",
      );
    } finally {
      setGeneratingInvoiceFor(null);
    }
  };

  const updateInvoicePdfEdit = (field, value) => {
    setInvoicePdfEdits((current) => ({ ...current, [field]: value }));
    setInvoiceDirty(true);
  };

  const applySignature = (dataUrl) => {
    const nextEdits = { ...invoicePdfEdits, signatureImage: dataUrl };
    setInvoicePdfEdits(nextEdits);
    if (invoicePreview)
      loadInvoicePdfPreview(invoicePreview.invoiceId, nextEdits);
  };

  const handleSignatureUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (!["image/png", "image/jpeg"].includes(file.type)) {
      setInvoicePdfError("Upload a PNG or JPG signature image.");
      return;
    }
    if (file.size > 3 * 1024 * 1024) {
      setInvoicePdfError("The signature image must be smaller than 3 MB.");
      return;
    }
    try {
      const dataUrl = await prepareSignatureImage(file);
      try {
        localStorage.setItem(SIGNATURE_STORAGE_KEY, dataUrl);
      } catch {
        /* storage full or blocked: signature still applies to this PDF */
      }
      applySignature(dataUrl);
    } catch (err) {
      setInvoicePdfError(err.message || "Unable to use this signature image.");
    }
  };

  const handleRemoveSignature = () => {
    try {
      localStorage.removeItem(SIGNATURE_STORAGE_KEY);
    } catch {
      /* ignore */
    }
    applySignature("");
  };

  const handleRefreshInvoicePreview = () => {
    if (invoicePreview)
      loadInvoicePdfPreview(invoicePreview.invoiceId, invoicePdfEdits);
  };

  const handleDownloadFinalInvoice = async () => {
    if (!invoicePreview) return;
    setInvoicePdfLoading(true);
    setInvoicePdfError("");
    try {
      const response = await axios.post(
        `${API}/invoice/${invoicePreview.invoiceId}/pdf-preview`,
        { preview: false, pdfEdits: invoicePdfEdits },
        { headers: getHeaders(), responseType: "blob" },
      );
      const url = window.URL.createObjectURL(
        new Blob([response.data], { type: "application/pdf" }),
      );
      const link = document.createElement("a");
      link.href = url;
      link.download = `Invoice_${invoicePreview.month}_${String(invoicePreview.clientId).slice(-6)}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setSuccessMessage(
        `Final invoice PDF downloaded for ${invoicePreview.clientLabel}.`,
      );
    } catch (err) {
      setInvoicePdfError(
        await getBlobErrorMessage(
          err,
          "Unable to download the final invoice PDF.",
        ),
      );
    } finally {
      setInvoicePdfLoading(false);
    }
  };

  const closeInvoicePreview = () => {
    previewReqRef.current += 1; // drop any in-flight preview
    setInvoicePreview(null);
    setInvoicePdfEdits({});
    setInvoicePdfError("");
    setInvoicePdfLoading(false);
    setInvoiceDirty(false);
    setInvoicePdfUrl((oldUrl) => {
      if (oldUrl) window.URL.revokeObjectURL(oldUrl);
      return "";
    });
  };

  /* ---------- Actions: reconciliation ---------- */
  // Preview: POST /api/mis/reconciliation/preview
  // Apply:   POST /api/mis/reconciliation/apply
  const handleReconciliationPreview = async (event) => {
    event.preventDefault();
    if (!clientId) {
      setReconciliationError(
        "Open a specific client's MIS page before reconciling.",
      );
      return;
    }
    if (!reconciliationFile) {
      setReconciliationError("Choose an Excel or CSV file first.");
      return;
    }

    const formData = new FormData();
    formData.append("file", reconciliationFile);
    formData.append("clientId", clientId);

    setReconciliationLoading(true);
    setReconciliationError("");
    setReconciliationReport(null);
    setReconciliationSelected({});
    try {
      const response = await axios.post(
        `${API}/reconciliation/preview`,
        formData,
        {
          // No Content-Type here: the browser adds the multipart boundary.
          headers: authHeaders(),
        },
      );
      setReconciliationReport(response.data);
      const hasIssues =
        (response.data?.counts?.mismatches ?? 0) +
          (response.data?.counts?.missingInMIS ?? 0) +
          (response.data?.counts?.missingInExcel ?? 0) +
          (response.data?.counts?.duplicates ?? 0) >
        0;
      setReconFilter(hasIssues ? "issues" : "all");
      setShowReconciliationModal(true);
    } catch (err) {
      setReconciliationError(
        err.response?.data?.message ||
          "Could not compare this file with the client's MIS.",
      );
    } finally {
      setReconciliationLoading(false);
    }
  };

  const reconciliationChangeKey = (row, field) =>
    `${row.misId}:${row.recordId}:${field.key}`;

  const toggleReconciliationField = (row, field, checked) => {
    const key = reconciliationChangeKey(row, field);
    setReconciliationSelected((current) => {
      const next = { ...current };
      if (checked) {
        next[key] = {
          misId: row.misId,
          recordId: row.recordId,
          field: field.key,
          value: field.excelValue,
        };
      } else {
        delete next[key];
      }
      return next;
    });
  };

  const selectAllMismatches = () => {
    const next = {};
    visibleReconRows.forEach((row) => {
      if (!row.misId || !row.recordId) return;
      (row.fields || []).forEach((field) => {
        if (field.status === "mismatch") {
          next[reconciliationChangeKey(row, field)] = {
            misId: row.misId,
            recordId: row.recordId,
            field: field.key,
            value: field.excelValue,
          };
        }
      });
    });
    setReconciliationSelected((current) => ({ ...current, ...next }));
  };

  const closeReconciliation = () => {
    if (!reconciliationApplying) setShowReconciliationModal(false);
  };

  const applyReconciliationSelected = async () => {
    const updatesByRecord = {};
    Object.values(reconciliationSelected).forEach(
      ({ misId, recordId, field, value }) => {
        const key = `${misId}:${recordId}`;
        if (!updatesByRecord[key]) {
          updatesByRecord[key] = { misId, recordId, fields: {} };
        }
        updatesByRecord[key].fields[field] = value;
      },
    );
    const updates = Object.values(updatesByRecord);
    if (!updates.length) return;

    const ok = await askConfirm({
      title: "Apply Excel values to MIS?",
      message: `${selectedCount} field change(s) across ${updates.length} record(s) will overwrite the current MIS values.`,
      confirmLabel: "Apply changes",
      danger: true,
    });
    if (!ok) return;

    setReconciliationApplying(true);
    setReconciliationError("");
    try {
      const response = await axios.post(
        `${API}/reconciliation/apply`,
        { updates },
        { headers: getHeaders() },
      );
      const failures = (response.data?.results || []).filter((r) => !r.success);
      if (failures.length) {
        setReconciliationError(
          `${failures.length} record(s) could not be updated. The rest were saved. Close this review and refresh MIS to check.`,
        );
        await fetchClientDetails();
      } else {
        setShowReconciliationModal(false);
        setReconciliationReport(null);
        setReconciliationSelected({});
        setReconciliationFile(null);
        setFileInputKey((k) => k + 1); // clears the file input
        await fetchClientDetails();
        setSuccessMessage(
          response.data?.message || "Selected reconciliation changes applied.",
        );
      }
    } catch (err) {
      setReconciliationError(
        err.response?.data?.message ||
          "Failed to apply the selected reconciliation changes.",
      );
    } finally {
      setReconciliationApplying(false);
    }
  };

  /* ---------- Inline cell editing ---------- */
  const getFieldValue = (item, field) => {
    const value = item[field];
    if (field === "vehicleId" || field === "driverId") return getId(value);
    return value ?? (["startingKm", "closingKm"].includes(field) ? "" : 0);
  };

  const beginCellEdit = (item, field) => {
    cancelEditRef.current = false;
    setActiveCell({ id: item._id, field });
    setCellValue(String(getFieldValue(item, field)));
    setError("");
    setSuccessMessage("");
  };

  const saveCell = async (item, field, rawValue) => {
    if (cancelEditRef.current) {
      cancelEditRef.current = false;
      return;
    }
    if (activeCell?.id !== item._id || activeCell?.field !== field) return;
    const originalValue = getFieldValue(item, field);
    if (String(rawValue ?? "") === String(originalValue ?? "")) {
      setActiveCell(null);
      return;
    }

    const payload = {};
    if (field === "vehicleId" || field === "driverId") {
      if (!String(rawValue).trim() && field === "driverId") {
        payload[field] = null;
      } else if (!String(rawValue).trim()) {
        setError("Vehicle ID can't be empty.");
        setActiveCell(null);
        return;
      } else {
        payload[field] = String(rawValue).trim();
      }
    } else if (rawValue === "" && ["startingKm", "closingKm"].includes(field)) {
      payload[field] = null;
    } else {
      const numberValue = Number(rawValue);
      if (rawValue === "" || !Number.isFinite(numberValue) || numberValue < 0) {
        setError("Enter a valid number of 0 or more.");
        setActiveCell(null);
        return;
      }
      payload[field] = numberValue;
    }

    setSavingCell(`${item._id}:${field}`);
    setError("");
    setSuccessMessage("");
    try {
      const response = await axios.patch(
        `${API}/update/${item._misId}/vehicle-records/${item._id}`,
        payload,
        { headers: getHeaders() },
      );
      const updatedParent = response.data?.data;
      const updatedRecord = updatedParent?.vehicleRecords?.find(
        (record) => String(record._id) === String(item._id),
      );
      setDetails((current) =>
        current.map((record) =>
          record._id === item._id
            ? updatedRecord
              ? {
                  ...updatedRecord,
                  _misId: item._misId,
                  _clientId: item._clientId,
                  _month: item._month,
                }
              : { ...record, ...payload }
            : record,
        ),
      );
      setSuccessMessage(response.data.message || "Saved.");
      setActiveCell(null);
    } catch (err) {
      console.error("MIS cell update error:", err);
      setError(err.response?.data?.message || `Unable to save ${field}.`);
      setActiveCell(null);
    } finally {
      setSavingCell(null);
    }
  };

  const toggleVehicleMISStatus = async (item) => {
    const nextStatus = item.status === "exclude" ? "include" : "exclude";
    const ok = await askConfirm({
      title:
        nextStatus === "exclude"
          ? "Exclude this entry?"
          : "Include this entry?",
      message:
        nextStatus === "exclude"
          ? "It stays visible in the table but is left out of the totals and the Excel export."
          : "It will count towards the totals and the Excel export again.",
      confirmLabel: nextStatus === "exclude" ? "Exclude" : "Include",
      danger: nextStatus === "exclude",
    });
    if (!ok) return;

    const rowKey = `${item._misId}:${item._id}`;
    setSavingVehicleStatus(rowKey);
    setError("");
    setSuccessMessage("");
    try {
      const response = await axios.patch(
        `${API}/${item._misId}/vehicle-records/${item._id}/status`,
        { status: nextStatus },
        { headers: getHeaders() },
      );
      const returnedRecord = response.data?.data?.vehicleRecords?.find(
        (record) => String(record._id) === String(item._id),
      );
      setDetails((current) =>
        current.map((record) =>
          String(record._id) === String(item._id)
            ? { ...record, status: returnedRecord?.status || nextStatus }
            : record,
        ),
      );
      setSuccessMessage(
        response.data?.message ||
          (nextStatus === "exclude" ? "Entry excluded." : "Entry included."),
      );
    } catch (err) {
      console.error("Vehicle MIS status update error:", err);
      setError(
        err.response?.data?.message ||
          `Unable to ${nextStatus} this vehicle MIS entry.`,
      );
    } finally {
      setSavingVehicleStatus(null);
    }
  };

  /* ---------- Export ---------- */
  const handleExportExcel = async (exportType = "summary") => {
    setExporting(true);
    setError("");
    try {
      let rows;
      let fileName;
      if (!clientId) {
        rows = filteredClients.map((client, index) => ({
          "S. No.": index + 1,
          Month: client.month || month,
          Client: client.clientName || getClientName(client.clientId),
          "MIS Sent": client.misSent ? "Yes" : "No",
          "MIS Sent At": formatDateTime(client.misSentAt),
          "MIS Approved": client.misApproved ? "Yes" : "No",
          "MIS Approved At": formatDateTime(client.misApprovedAt),
          "Invoice Generated": client.invoiceGenerated ? "Yes" : "No",
          "Invoice Generated At": formatDateTime(client.invoiceGeneratedAt),
          "Invoice Sent": client.invoiceSent ? "Yes" : "No",
          "Invoice Sent At": formatDateTime(client.invoiceSentAt),
          "Vehicle Entries": Number(client.totalEntries ?? 0),
          "Total KM": Number(Number(client.totalKm || 0).toFixed(2)),
          "Total Payout": Number(Number(client.totalPayout || 0).toFixed(2)),
          "Total Toll": Number(Number(client.totalToll || 0).toFixed(2)),
          "Total Parking": Number(Number(client.totalParking || 0).toFixed(2)),
          "Total MCD": Number(Number(client.totalMcd || 0).toFixed(2)),
          "Total OT Hours": Number(Number(client.totalOT || 0).toFixed(2)),
          "Total OT Amount": Number(
            Number(client.totalOTAmount || 0).toFixed(2),
          ),
          "Total Extra KM": Number(Number(client.totalExtraKm || 0).toFixed(2)),
          "Total Extra KM Amount": Number(
            Number(client.totalExtraKmAmount || 0).toFixed(2),
          ),
          "Total Expense": Number((
            (Number(client.totalPayout) || 0) +
            (Number(client.totalToll) || 0) +
            (Number(client.totalParking) || 0) +
            (Number(client.totalMcd) || 0) +
            (Number(client.totalOTAmount) || 0) +
            (Number(client.totalExtraKmAmount) || 0)
          ).toFixed(2)),
        }));
        fileName = `MIS_Summary_${month}.xlsx`;
      } else {
        // Excluded records are visible in the UI but never exported.
        rows = filteredDetails
          .filter((item) => item.status !== "exclude")
          .map((item, index) => ({
            "S. No.": index + 1,
            Month: item._month || month,
            Client: clientName,
            Date: formatDate(item.date),
            Vehicle: getVehicleName(item.vehicleId),
            Driver: getDriverName(item.driverId),
            "Starting KM": item.startingKm ?? "",
            "Closing KM": item.closingKm ?? "",
            "Total KM": Number(Number(item.totalKm || 0).toFixed(2)),
            Payout: Number(Number(item.payout || 0).toFixed(2)),
            Toll: Number(item.toll || 0),
            Parking: Number(item.parking || 0),
            MCD: Number(item.mcd || 0),
            OT: Number(item.ot || 0),
            "OT Amount": Number(item.otAmount || 0),
            "Extra KM": Number(item.extraKm || 0),
            "Extra KM Amount": Number(item.extraKmAmount || 0),
            "Total Expense": Number((
              (Number(item.payout) || 0) +
              (Number(item.toll) || 0) +
              (Number(item.parking) || 0) +
              (Number(item.mcd) || 0) +
              (Number(item.otAmount) || 0) +
              (Number(item.extraKmAmount) || 0)
            ).toFixed(2)),
          }));
        fileName = `MIS_${(clientName || "Client").replace(/[^a-z0-9-_]/gi, "_")}_${month}.xlsx`;
      }

      if (!rows.length) {
        setError("There's no MIS data to export for this month.");
        return;
      }
      const workbook = XLSX.utils.book_new();

      const appendSheet = (sheetRows, sheetName, addTotals = false) => {
        if (!sheetRows.length) return;

        // Add a totals footer to detailed MIS sheets so each vehicle's trips,
        // kilometres and expenses can be checked without leaving the sheet.
        const exportRows = [...sheetRows];
        if (addTotals && "Date" in sheetRows[0]) {
          const sumField = (field) => Number(
            sheetRows.reduce((total, row) => total + (Number(row[field]) || 0), 0).toFixed(2),
          );
          exportRows.push({
            "S. No.": "TOTAL",
            Month: "",
            Client: "",
            Date: `Total Trips: ${sheetRows.length}`,
            Vehicle: "",
            Driver: "",
            "Starting KM": "",
            "Closing KM": "",
            "Total KM": sumField("Total KM"),
            Payout: sumField("Payout"),
            Toll: sumField("Toll"),
            Parking: sumField("Parking"),
            MCD: sumField("MCD"),
            OT: sumField("OT"),
            "OT Amount": sumField("OT Amount"),
            "Extra KM": sumField("Extra KM"),
            "Extra KM Amount": sumField("Extra KM Amount"),
            "Total Expense": sumField("Total Expense"),
          });
        } else if (addTotals && "Vehicle Entries" in sheetRows[0]) {
          const sumField = (field) => Number(
            sheetRows.reduce((total, row) => total + (Number(row[field]) || 0), 0).toFixed(2),
          );
          exportRows.push({
            "S. No.": "TOTAL",
            Month: "",
            Client: "",
            "MIS Sent": "",
            "MIS Sent At": "",
            "MIS Approved": "",
            "MIS Approved At": "",
            "Invoice Generated": "",
            "Invoice Generated At": "",
            "Invoice Sent": "",
            "Invoice Sent At": "",
            "Vehicle Entries": sumField("Vehicle Entries"),
            "Total KM": sumField("Total KM"),
            "Total Payout": sumField("Total Payout"),
            "Total Toll": sumField("Total Toll"),
            "Total Parking": sumField("Total Parking"),
            "Total MCD": sumField("Total MCD"),
            "Total OT Hours": sumField("Total OT Hours"),
            "Total OT Amount": sumField("Total OT Amount"),
            "Total Extra KM": sumField("Total Extra KM"),
            "Total Extra KM Amount": sumField("Total Extra KM Amount"),
            "Total Expense": sumField("Total Expense"),
          });
        }

        const worksheet = XLSX.utils.json_to_sheet(exportRows);
        worksheet["!cols"] = Object.keys(sheetRows[0]).map((key) => ({
          wch: Math.min(
            28,
            Math.max(
              key.length + 2,
              ...exportRows
                .slice(0, 50)
                .map((row) => String(row[key] ?? "").length + 2),
            ),
          ),
        }));
        // Excel sheet names cannot exceed 31 characters or contain these characters.
        const safeName = String(sheetName || "Vehicle")
          .replace(/[\\/?*:\[\]]/g, "_")
          .slice(0, 31) || "Vehicle";
        let uniqueName = safeName;
        let suffix = 2;
        while (workbook.SheetNames.includes(uniqueName)) {
          uniqueName = `${safeName.slice(0, 31 - String(suffix).length - 1)}_${suffix++}`;
        }
        XLSX.utils.book_append_sheet(workbook, worksheet, uniqueName);
      };

      if (clientId && exportType === "vehicle") {
        const groupedRows = rows.reduce((groups, row) => {
          const vehicleNumber = String(row.Vehicle || "Unknown Vehicle").trim();
          if (!groups.has(vehicleNumber)) groups.set(vehicleNumber, []);
          groups.get(vehicleNumber).push(row);
          return groups;
        }, new Map());

        groupedRows.forEach((vehicleRows, vehicleNumber) => {
          appendSheet(vehicleRows, vehicleNumber, true);
        });
      } else {
        appendSheet(rows, clientId ? "MIS Details" : "Client Summary", true);
      }

      XLSX.writeFile(workbook, fileName);
      setShowExportOptions(false);
      setSuccessMessage(
        exportType === "vehicle" && clientId
          ? `Exported ${rows.length} row(s) across ${workbook.SheetNames.length} vehicle sheet(s) to ${fileName}.`
          : `Exported ${rows.length} row(s) to ${fileName}.`,
      );
    } catch (err) {
      console.error("MIS Excel export error:", err);
      setError("Unable to export MIS to Excel.");
    } finally {
      setExporting(false);
    }
  };

  /* ---------- Render helpers ---------- */
  const renderEditableCell = (item, field, display, type = "number") => {
    const isActive = activeCell?.id === item._id && activeCell?.field === field;
    const isSaving = savingCell === `${item._id}:${field}`;
    const wide = field === "vehicleId" || field === "driverId";
    return (
      <td
        key={field}
        className={[
          "mis-cell",
          wide ? "mis-cell--wide" : "",
          type === "number" ? "num" : "",
          isActive ? "is-active" : "",
          isSaving ? "is-saving" : "",
        ].join(" ")}
        tabIndex={isActive ? -1 : 0}
        onClick={() => {
          if (!isActive && !isSaving && !savingCell) beginCellEdit(item, field);
        }}
        onKeyDown={(e) => {
          if (
            e.target === e.currentTarget &&
            e.key === "Enter" &&
            !isActive &&
            !savingCell
          ) {
            e.preventDefault();
            beginCellEdit(item, field);
          }
        }}
        title="Click to edit"
      >
        {isSaving ? (
          <span className="small">Saving…</span>
        ) : isActive ? (
          <input
            autoFocus
            onFocus={(e) => e.target.select()}
            type={type}
            className="form-control form-control-sm"
            value={cellValue}
            min={type === "number" ? "0" : undefined}
            step={type === "number" ? "any" : undefined}
            onChange={(e) => setCellValue(e.target.value)}
            onBlur={() => saveCell(item, field, cellValue)}
            onKeyDown={(e) => {
              if (e.key === "Enter") e.currentTarget.blur();
              if (e.key === "Escape") {
                cancelEditRef.current = true;
                setActiveCell(null);
              }
            }}
            onClick={(e) => e.stopPropagation()}
            aria-label={`Edit ${field}`}
          />
        ) : (
          <span className="mis-cell-text">
            {display ?? <span className="mis-cell-empty">—</span>}
          </span>
        )}
      </td>
    );
  };

  const firstPendingStep = STATUS_STEPS.findIndex(
    (s) => !misTimeline?.[s.field],
  );

  /* ---------------------------------------------------------------- */
  /* Render                                                            */
  /* ---------------------------------------------------------------- */
  return (
    <>
      <Layouts />
      <div className="mis-page">
        <div className="mis-shell">
          {/* Header */}
          <header className="mis-head">
            <div>
              <h1 className="mis-title">
                {clientId ? clientName : "Monthly MIS"}
              </h1>
              <p className="mis-subtitle">
                {clientId
                  ? "Vehicle-wise records for this client and month"
                  : "Client-wise monthly MIS and invoice status"}
              </p>
            </div>
            <div className="mis-actions">
              {clientId && (
                <>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => setShowSendMIS(true)}
                    disabled={loading || details.length === 0}
                  >
                    Send MIS
                  </button>
                  <button
                    type="button"
                    className="btn btn-success"
                    onClick={() => setShowSendInvoice(true)}
                    disabled={loading || details.length === 0}
                  >
                    Send Invoice
                  </button>
                </>
              )}
              <button
                type="button"
                className="btn btn-outline-primary"
                onClick={() => setInboxPurpose("mis")}
              >
                MIS Inbox
              </button>
              <button
                type="button"
                className="btn btn-outline-primary"
                onClick={() => setInboxPurpose("accounts")}
              >
                Accounts Inbox
              </button>
              {clientId && (
                <Link
                  to={`/mis?month=${month}`}
                  className="btn btn-outline-primary"
                >
                  ← All clients
                </Link>
              )}
              <button
                type="button"
                className="btn btn-success"
                onClick={() => {
                  if (clientId) setShowExportOptions(true);
                  else handleExportExcel("summary");
                }}
                disabled={
                  exporting ||
                  loading ||
                  (clientId
                    ? details.length === 0
                    : filteredClients.length === 0)
                }
              >
                {exporting ? "Exporting…" : "Export Excel"}
              </button>
            </div>
          </header>

          {showExportOptions && clientId && (
            <div
              className="mis-backdrop"
              role="presentation"
              onMouseDown={(event) => {
                if (event.target === event.currentTarget && !exporting) {
                  setShowExportOptions(false);
                }
              }}
              style={{
                position: "fixed",
                inset: 0,
                zIndex: 100020,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 16,
                background: "rgba(20,22,40,.55)",
              }}
            >
              <section
                className="mis-card mis-card-pad"
                role="dialog"
                aria-modal="true"
                aria-labelledby="mis-export-title"
                style={{ width: "min(100%, 460px)", background: "white" }}
              >
                <h3 id="mis-export-title">Export MIS to Excel</h3>
                <p className="text-muted">
                  Choose how you want your Excel report to be organised.
                </p>
                <div className="d-flex flex-column gap-2">
                  <button
                    type="button"
                    className="btn btn-success"
                    disabled={exporting}
                    onClick={() => handleExportExcel("summary")}
                  >
                    {exporting ? "Exporting…" : "Download Summary"}
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-success"
                    disabled={exporting}
                    onClick={() => handleExportExcel("vehicle")}
                  >
                    {exporting ? "Exporting…" : "Download Vehicle-wise Summary"}
                  </button>
                  <button
                    type="button"
                    className="btn btn-link"
                    disabled={exporting}
                    onClick={() => setShowExportOptions(false)}
                  >
                    Cancel
                  </button>
                </div>
              </section>
            </div>
          )}

          {/* Toolbar */}
          <section className="mis-card mis-card-pad">
            <div className="mis-toolbar">
              <div className="mis-field">
                <label className="form-label" htmlFor="mis-month">
                  Month
                </label>
                <input
                  id="mis-month"
                  type="month"
                  className="form-control"
                  value={month}
                  onChange={(e) => {
                    if (!e.target.value) return;
                    setMonth(e.target.value);
                    setSummaryPage(1);
                    setDetailDateFrom("");
                    setDetailDateTo("");
                  }}
                />
              </div>

              {!clientId && (
                <>
                  <div className="mis-field mis-field--grow">
                    <label className="form-label" htmlFor="mis-search">
                      Find a client
                    </label>
                    <input
                      id="mis-search"
                      type="search"
                      className="form-control"
                      placeholder="Search by client name"
                      value={clientSearch}
                      onChange={(e) => {
                        setClientSearch(e.target.value);
                        setSummaryPage(1);
                      }}
                    />
                  </div>
                  <div className="mis-field">
                    <label className="form-label" htmlFor="mis-date">
                      Generate daily MIS (IST)
                    </label>
                    <div className="input-group">
                      <input
                        id="mis-date"
                        type="date"
                        className="form-control"
                        value={date}
                        onChange={(e) => {
                          setDate(e.target.value);
                          setError("");
                          setSuccessMessage("");
                        }}
                      />
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleGenerateMIS}
                        disabled={loading || generating || !date}
                      >
                        {generating ? "Generating…" : "Generate"}
                      </button>
                    </div>
                  </div>
                </>
              )}

              {clientId && (
                <>
                  <div className="mis-field">
                    <label className="form-label" htmlFor="mis-detail-date-from">
                      Date from
                    </label>
                    <input
                      id="mis-detail-date-from"
                      type="date"
                      className="form-control"
                      value={detailDateFrom}
                      max={detailDateTo || undefined}
                      onChange={(e) => setDetailDateFrom(e.target.value)}
                    />
                  </div>
                  <div className="mis-field">
                    <label className="form-label" htmlFor="mis-detail-date-to">
                      Date to
                    </label>
                    <input
                      id="mis-detail-date-to"
                      type="date"
                      className="form-control"
                      value={detailDateTo}
                      min={detailDateFrom || undefined}
                      onChange={(e) => setDetailDateTo(e.target.value)}
                    />
                  </div>
                  {(detailDateFrom || detailDateTo) && (
                    <div className="mis-field">
                      <label className="form-label">&nbsp;</label>
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => {
                          setDetailDateFrom("");
                          setDetailDateTo("");
                        }}
                      >
                        Clear dates
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </section>

          {/* Notices */}
          {error && (
            <div className="mis-notice mis-notice--error" role="alert">
              <span>{error}</span>
              <button
                type="button"
                aria-label="Dismiss"
                onClick={() => setError("")}
              >
                ×
              </button>
            </div>
          )}
          {successMessage && (
            <div className="mis-notice mis-notice--success" role="status">
              <span>{successMessage}</span>
              <button
                type="button"
                aria-label="Dismiss"
                onClick={() => setSuccessMessage("")}
              >
                ×
              </button>
            </div>
          )}

          {loading ? (
            <div
              className="mis-card mis-skeleton"
              aria-busy="true"
              aria-label="Loading MIS data"
            >
              {[...Array(7)].map((_, i) => (
                <span key={i} />
              ))}
            </div>
          ) : clientId ? (
            <>
              {/* Reconcile */}
              <section className="mis-card mis-card-pad">
                <form
                  className="mis-upload"
                  onSubmit={handleReconciliationPreview}
                >
                  <div style={{ flex: "1 1 320px" }}>
                    <label className="form-label" htmlFor="mis-recon-file">
                      Reconcile with the client's Excel
                    </label>
                    <input
                      key={fileInputKey}
                      id="mis-recon-file"
                      className="form-control"
                      type="file"
                      accept=".xlsx,.xls,.csv"
                      onChange={(event) => {
                        setReconciliationFile(event.target.files?.[0] || null);
                        setReconciliationError("");
                      }}
                    />
                    <div className="mis-upload-hint">
                      Rows match on vehicle and date. Payout, OT hours, extra
                      KM, toll, parking, MCD and the KM readings are compared.
                      Nothing changes until you apply it.
                    </div>
                  </div>
                  <button
                    className="btn btn-primary"
                    type="submit"
                    disabled={reconciliationLoading || !reconciliationFile}
                  >
                    {reconciliationLoading ? "Comparing…" : "Upload and review"}
                  </button>
                </form>
                {reconciliationError && !showReconciliationModal && (
                  <div
                    className="mis-notice mis-notice--error mt-3"
                    role="alert"
                  >
                    <span>{reconciliationError}</span>
                  </div>
                )}
              </section>

              {/* Progress */}
              <section
                className="mis-card mis-card-pad"
                aria-label="MIS and invoice progress"
              >
                <ol className="mis-steps">
                  {STATUS_STEPS.map((step, index) => {
                    const done = Boolean(misTimeline?.[step.field]);
                    return (
                      <li
                        key={step.field}
                        className={`mis-step ${done ? "is-done" : ""} ${
                          index === firstPendingStep ? "is-current" : ""
                        }`}
                      >
                        <span className="mis-step-dot" aria-hidden="true">
                          {done ? "✓" : ""}
                        </span>
                        <div className="mis-step-label">{step.label}</div>
                        <div className="mis-step-meta">
                          {done
                            ? formatDateTime(misTimeline?.[step.at])
                            : "Pending"}
                        </div>
                      </li>
                    );
                  })}
                </ol>
              </section>

              {/* Key figures */}
              <dl className="mis-figures">
                {[
                  ["Payout", formatMoney(detailTotals.payout), true],
                  ["Trips", detailTotals.trips.toLocaleString("en-IN")],
                  ["Total KM", formatNumber(detailTotals.totalKm)],
                  ["Toll", formatMoney(detailTotals.toll)],
                  ["Parking", formatMoney(detailTotals.parking)],
                  ["MCD", formatMoney(detailTotals.mcd)],
                  ["OT hours", formatNumber(detailTotals.ot)],
                  ["OT amount", formatMoney(detailTotals.otAmount)],
                  ["Extra KM", formatNumber(detailTotals.extraKm)],
                  ["Extra KM amount", formatMoney(detailTotals.extraKmAmount)],
                ].map(([label, value, lead]) => (
                  <div
                    className={`mis-figure ${lead ? "mis-figure--lead" : ""}`}
                    key={label}
                  >
                    <dt>{label}</dt>
                    <dd>{value}</dd>
                  </div>
                ))}
              </dl>

              <div className="mis-table-hint">
                Click a cell, or focus it and press Enter, to edit. Enter or
                leaving the cell saves; Escape cancels.
              </div>

              <div className="d-flex flex-wrap align-items-end justify-content-between gap-3 mb-3">
                <div className="mis-field" style={{ minWidth: 150, maxWidth: 190 }}>
                  <label className="form-label" htmlFor="mis-detail-page-size">
                    Records per page
                  </label>
                  <select
                    id="mis-detail-page-size"
                    className="form-select"
                    value={detailPageSize}
                    onChange={(e) => setDetailPageSize(Number(e.target.value))}
                  >
                    {[10, 25, 50, 100].map((size) => (
                      <option key={size} value={size}>{size}</option>
                    ))}
                  </select>
                </div>
                <div className="d-flex flex-wrap align-items-center gap-2">
                  <span className="small text-muted">
                    {filteredDetails.length
                      ? `Showing ${(detailPage - 1) * detailPageSize + 1}–${Math.min(detailPage * detailPageSize, filteredDetails.length)} of ${filteredDetails.length} records`
                      : "Showing 0 records"}
                  </span>
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    disabled={detailPage <= 1 || !filteredDetails.length}
                    onClick={() => setDetailPage((page) => Math.max(1, page - 1))}
                  >
                    Previous
                  </button>
                  <span className="small" aria-live="polite">
                    Page {filteredDetails.length ? detailPage : 0} of {filteredDetails.length ? detailPageCount : 0}
                  </span>
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    disabled={detailPage >= detailPageCount || !filteredDetails.length}
                    onClick={() => setDetailPage((page) => Math.min(detailPageCount, page + 1))}
                  >
                    Next
                  </button>
                </div>
              </div>

              <div className="mis-table-wrap">
                <table className="mis-table">
                  <thead>
                    <tr>
                      <th
                        className="sticky-col"
                        style={{ left: 0, minWidth: 48 }}
                      >
                        #
                      </th>
                      <th
                        className="sticky-col"
                        style={{ left: 48, minWidth: 120 }}
                      >
                        Date
                      </th>
                      <th>Vehicle</th>
                      <th>Driver ID</th>
                      <th className="num">Starting KM</th>
                      <th className="num">Closing KM</th>
                      <th className="num">Total KM</th>
                      <th className="num">Payout</th>
                      <th className="num">Toll</th>
                      <th className="num">Parking</th>
                      <th className="num">MCD</th>
                      <th className="num">OT</th>
                      <th className="num">OT amount</th>
                      <th className="num">Extra KM</th>
                      <th className="num">Extra KM amount</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedDetails.length ? (
                      paginatedDetails.map((item, index) => {
                        const excluded = item.status === "exclude";
                        const rowSaving =
                          savingVehicleStatus === `${item._misId}:${item._id}`;
                        return (
                          <tr
                            key={item._id}
                            className={excluded ? "is-excluded" : ""}
                          >
                            <td className="sticky-col" style={{ left: 0 }}>
                              {(detailPage - 1) * detailPageSize + index + 1}
                            </td>
                            <td
                              className="sticky-col"
                              style={{ left: 48, whiteSpace: "nowrap" }}
                            >
                              {formatDate(item.date)}
                            </td>
                            <td style={{ whiteSpace: "nowrap" }}>
                              {getVehicleName(item.vehicleId)}
                            </td>
                            <td style={{ whiteSpace: "nowrap" }}>
                              {getDriverName(item.driverId)}
                            </td>
                            {renderEditableCell(
                              item,
                              "startingKm",
                              formatNullableNumber(item.startingKm),
                            )}
                            {renderEditableCell(
                              item,
                              "closingKm",
                              formatNullableNumber(item.closingKm),
                            )}
                            <td className="num">
                              {formatNumber(item.totalKm)}
                            </td>
                            {renderEditableCell(
                              item,
                              "payout",
                              formatMoney(item.payout),
                            )}
                            {renderEditableCell(
                              item,
                              "toll",
                              formatMoney(item.toll),
                            )}
                            {renderEditableCell(
                              item,
                              "parking",
                              formatMoney(item.parking),
                            )}
                            {renderEditableCell(
                              item,
                              "mcd",
                              formatMoney(item.mcd),
                            )}
                            {renderEditableCell(
                              item,
                              "ot",
                              formatNumber(item.ot),
                            )}
                            <td className="num">
                              {formatMoney(item.otAmount)}
                            </td>
                            {renderEditableCell(
                              item,
                              "extraKm",
                              formatNumber(item.extraKm),
                            )}
                            <td className="num">
                              {formatMoney(item.extraKmAmount)}
                            </td>
                            <td>
                              <span
                                className={`mis-badge ${excluded ? "mis-badge--neutral" : "mis-badge--success"}`}
                              >
                                {excluded ? "Excluded" : "Included"}
                              </span>
                            </td>
                            <td style={{ minWidth: 105 }}>
                              <button
                                type="button"
                                className={`btn btn-sm ${excluded ? "btn-success" : "btn-outline-danger"}`}
                                disabled={rowSaving}
                                onClick={() => toggleVehicleMISStatus(item)}
                              >
                                {rowSaving
                                  ? "Saving…"
                                  : excluded
                                    ? "Include"
                                    : "Exclude"}
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="17">
                          <div className="mis-empty">
                            <strong>
                              {details.length
                                ? "No records match the selected date filter"
                                : "No vehicle records yet"}
                            </strong>
                            {details.length
                              ? "Adjust or clear the date range to see more records."
                              : `Generate the daily MIS for a date in ${month} to see entries here.`}
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                  {filteredDetails.length > 0 && (
                    <tfoot>
                      <tr>
                        <td colSpan="6" className="text-end">
                          Total · {detailTotals.trips} trips
                        </td>
                        <td className="num">
                          {formatNumber(detailTotals.totalKm)}
                        </td>
                        <td className="num">
                          {formatMoney(detailTotals.payout)}
                        </td>
                        <td className="num">
                          {formatMoney(detailTotals.toll)}
                        </td>
                        <td className="num">
                          {formatMoney(detailTotals.parking)}
                        </td>
                        <td className="num">{formatMoney(detailTotals.mcd)}</td>
                        <td className="num">{formatNumber(detailTotals.ot)}</td>
                        <td className="num">
                          {formatMoney(detailTotals.otAmount)}
                        </td>
                        <td className="num">
                          {formatNumber(detailTotals.extraKm)}
                        </td>
                        <td className="num">
                          {formatMoney(detailTotals.extraKmAmount)}
                        </td>
                        <td colSpan="2" />
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
              <div className="mis-table-hint">
                Showing {filteredDetails.length} of {details.length} records for the selected date range:{" "}
                {detailTotals.trips} included, {excludedCount} excluded. Excluded entries stay visible but
                don't count in totals or the export.
              </div>
            </>
          ) : (
            <>
              {/* Summary figures */}
              <dl className="mis-figures">
                {[
                  ["Total payout", formatMoney(summaryTotals.payout), true],
                  ["Clients", filteredClients.length.toLocaleString("en-IN")],
                  ["Trips", summaryTotals.entries.toLocaleString("en-IN")],
                  ["Total KM", formatNumber(summaryTotals.km)],
                  [
                    "MIS approved",
                    `${summaryTotals.approved} / ${filteredClients.length}`,
                  ],
                  [
                    "Invoices generated",
                    `${summaryTotals.invoiced} / ${filteredClients.length}`,
                  ],
                ].map(([label, value, lead]) => (
                  <div
                    className={`mis-figure ${lead ? "mis-figure--lead" : ""}`}
                    key={label}
                  >
                    <dt>{label}</dt>
                    <dd>{value}</dd>
                  </div>
                ))}
              </dl>

              <div className="mis-table-wrap">
                <table className="mis-table">
                  <thead>
                    <tr>
                      <th
                        className="sticky-col"
                        style={{ left: 0, minWidth: 48 }}
                      >
                        #
                      </th>
                      <th
                        className="sticky-col"
                        style={{ left: 48, minWidth: 200 }}
                      >
                        Client
                      </th>
                      <th>Month</th>
                      <th className="num">Trips</th>
                      <th className="num">Total KM</th>
                      <th className="num">Payout</th>
                      <th className="num">Toll</th>
                      <th className="num">Parking</th>
                      <th className="num">MCD</th>
                      <th className="num">OT</th>
                      <th className="num">OT amount</th>
                      <th className="num">Extra KM</th>
                      <th className="num">Extra KM amount</th>
                      {STATUS_STEPS.map((s) => (
                        <th key={s.field} style={{ textAlign: "center" }}>
                          {s.label}
                        </th>
                      ))}
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedClients.length ? (
                      paginatedClients.map((client, index) => {
                        const id = getId(client.clientId);
                        const misId = client.misId || client._id;
                        const label =
                          client.clientName || getClientName(client.clientId);
                        const invoiceBusy = generatingInvoiceFor === String(id);
                        return (
                          <tr key={misId || id}>
                            <td className="sticky-col" style={{ left: 0 }}>
                              {(summaryPage - 1) * summaryPageSize + index + 1}
                            </td>
                            <td className="sticky-col" style={{ left: 48 }}>
                              <Link
                                className="mis-client-link"
                                to={`/mis/${id}?month=${month}`}
                              >
                                {label}
                              </Link>
                            </td>
                            <td>{client.month || month}</td>
                            <td className="num">{client.totalEntries ?? 0}</td>
                            <td className="num">
                              {formatNumber(client.totalKm)}
                            </td>
                            <td className="num">
                              {formatMoney(client.totalPayout)}
                            </td>
                            <td className="num">
                              {formatMoney(client.totalToll)}
                            </td>
                            <td className="num">
                              {formatMoney(client.totalParking)}
                            </td>
                            <td className="num">
                              {formatMoney(client.totalMcd)}
                            </td>
                            <td className="num">
                              {formatNumber(client.totalOT)}
                            </td>
                            <td className="num">
                              {formatMoney(client.totalOTAmount)}
                            </td>
                            <td className="num">
                              {formatNumber(client.totalExtraKm)}
                            </td>
                            <td className="num">
                              {formatMoney(client.totalExtraKmAmount)}
                            </td>

                            {STATUS_STEPS.map((s, stepIndex) => {
                              const previousStep = STATUS_STEPS[stepIndex - 1];

                              // A step is locked until the previous step is completed.
                              const isLocked =
                                stepIndex > 0 && !client[previousStep.field];

                              const isSaving =
                                savingStatus === `${misId}:${s.field}`;

                              return (
                                <td key={s.field} className="mis-status-cell">
                                  <div className="form-check form-switch d-flex justify-content-center m-0 p-0">
                                    <input
                                      type="checkbox"
                                      role="switch"
                                      className="form-check-input m-0"
                                      checked={Boolean(client[s.field])}
                                      disabled={isLocked || isSaving}
                                      onChange={(e) =>
                                        updateStatus(
                                          client,
                                          s.field,
                                          e.target.checked,
                                        )
                                      }
                                      aria-label={`${s.label} for ${label}`}
                                      title={
                                        isLocked
                                          ? `Complete ${previousStep.label} first`
                                          : s.label
                                      }
                                    />
                                  </div>

                                  <div className="mis-status-time">
                                    {client[s.field]
                                      ? formatDateTime(client[s.at])
                                      : isLocked
                                        ? `Waiting for ${previousStep.label}`
                                        : "Pending"}
                                  </div>
                                </td>
                              );
                            })}

                        <td className="mis-action-cell">
  <div className="mis-actions-grid">
    <div className="mis-actions-row">
      <Link
        className="btn btn-outline-primary btn-sm"
        to={`/mis/${id}?month=${month}`}
      >
        View details
      </Link>
      {client.invoicePdfUrl && (
        <a
          className="btn btn-outline-success btn-sm"
          href={client.invoicePdfUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          View invoice
        </a>
      )}
    </div>
    <button
      type="button"
      className="btn btn-success btn-sm mis-actions-primary"
      onClick={() => openInvoiceNumberModal(client)}
      disabled={invoiceBusy || !client.totalEntries || !client.misApproved}
      title={
        !client.totalEntries
          ? "No vehicle records to invoice"
          : !client.misApproved
            ? "Approve MIS before generating the invoice"
            : "Generate the invoice for this client and month"
      }
    >
      {invoiceBusy
        ? "Generating…"
        : client.invoiceGenerated
          ? "Regenerate invoice"
          : "Generate invoice"}
    </button>
  </div>
</td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="18">
                          <div className="mis-empty">
                            <strong>
                              {clientSearch
                                ? "No clients match your search"
                                : "No MIS records for this month"}
                            </strong>
                            {clientSearch
                              ? "Try a different name or clear the search."
                              : "Pick a date above and generate the daily MIS to get started."}
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                  {filteredClients.length > 0 && (
                    <tfoot>
                      <tr>
                        <td colSpan="3" className="text-end">
                          Total · {filteredClients.length} clients
                        </td>
                        <td className="num">{summaryTotals.entries}</td>
                        <td className="num">
                          {formatNumber(summaryTotals.km)}
                        </td>
                        <td className="num">
                          {formatMoney(summaryTotals.payout)}
                        </td>
                        <td className="num">
                          {formatMoney(summaryTotals.toll)}
                        </td>
                        <td className="num">
                          {formatMoney(summaryTotals.parking)}
                        </td>
                        <td className="num">
                          {formatMoney(summaryTotals.mcd)}
                        </td>
                        <td className="num">
                          {formatNumber(summaryTotals.ot)}
                        </td>
                        <td className="num">
                          {formatMoney(summaryTotals.otAmount)}
                        </td>
                        <td className="num">
                          {formatNumber(summaryTotals.extraKm)}
                        </td>
                        <td className="num">
                          {formatMoney(summaryTotals.extraKmAmount)}
                        </td>
                        <td colSpan="5" />
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>

              <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-3">
                <div className="d-flex align-items-center gap-2">
                  <label htmlFor="mis-summary-page-size" className="form-label mb-0">
                    Rows per page
                  </label>
                  <select
                    id="mis-summary-page-size"
                    className="form-select form-select-sm"
                    style={{ width: 90 }}
                    value={summaryPageSize}
                    onChange={(e) => {
                      setSummaryPageSize(Number(e.target.value));
                      setSummaryPage(1);
                    }}
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                  </select>
                  <span className="text-muted small">
                    {filteredClients.length
                      ? `Showing ${(summaryPage - 1) * summaryPageSize + 1}–${Math.min(summaryPage * summaryPageSize, filteredClients.length)} of ${filteredClients.length} clients`
                      : "No clients to display"}
                  </span>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm"
                    disabled={summaryPage <= 1 || !filteredClients.length}
                    onClick={() => setSummaryPage((page) => Math.max(1, page - 1))}
                  >
                    Previous
                  </button>
                  <span className="small">
                    Page {filteredClients.length ? summaryPage : 0} of {filteredClients.length ? summaryPageCount : 0}
                  </span>
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm"
                    disabled={summaryPage >= summaryPageCount || !filteredClients.length}
                    onClick={() => setSummaryPage((page) => Math.min(summaryPageCount, page + 1))}
                  >
                    Next
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Reconciliation review */}
      {showReconciliationModal && reconciliationReport && (
        <div
          className="mis-backdrop mis-page"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) closeReconciliation();
          }}
          style={{
            background: "rgba(20,22,40,.55)",
            minHeight: 0,
            padding: 16,
          }}
        >
          <div
            className="mis-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="mis-reconcile-title"
          >
            <div className="mis-dialog-head">
              <div>
                <h5 className="mb-1" id="mis-reconcile-title">
                  Review Excel against MIS
                </h5>
                <div className="small text-muted">
                  Tick the fields where the Excel value is correct. Only ticked
                  fields change; previewing never edits MIS.
                </div>
              </div>
              <button
                className="btn-close"
                type="button"
                aria-label="Close"
                onClick={closeReconciliation}
              />
            </div>

            <div className="mis-dialog-body">
              <div className="mis-recon-counts">
                {[
                  [
                    "Excel rows",
                    reconciliationReport.counts?.totalExcelRows ?? 0,
                    "",
                  ],
                  [
                    "Matched",
                    reconciliationReport.counts?.matched ?? 0,
                    "success",
                  ],
                  [
                    "Mismatches",
                    reconciliationReport.counts?.mismatches ?? 0,
                    "danger",
                  ],
                  [
                    "Missing in MIS",
                    reconciliationReport.counts?.missingInMIS ?? 0,
                    "warning",
                  ],
                  [
                    "Missing in Excel",
                    reconciliationReport.counts?.missingInExcel ?? 0,
                    "warning",
                  ],
                  [
                    "Duplicates",
                    reconciliationReport.counts?.duplicates ?? 0,
                    "danger",
                  ],
                ].map(([label, value, tone]) => (
                  <div
                    className={`mis-recon-count ${tone ? `mis-recon-count--${tone}` : ""}`}
                    key={label}
                  >
                    <span>{label}</span>
                    <strong>{value}</strong>
                  </div>
                ))}
              </div>

              <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                <div
                  className="mis-chips"
                  role="group"
                  aria-label="Filter rows"
                >
                  {RECON_FILTERS.filter(
                    (f) =>
                      ["issues", "all"].includes(f.key) ||
                      reconFilterCounts[f.key],
                  ).map((f) => (
                    <button
                      key={f.key}
                      type="button"
                      className={`mis-chip ${reconFilter === f.key ? "is-on" : ""}`}
                      onClick={() => setReconFilter(f.key)}
                    >
                      {f.label} ({reconFilterCounts[f.key] || 0})
                    </button>
                  ))}
                </div>
                <div className="d-flex gap-2">
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm"
                    onClick={selectAllMismatches}
                  >
                    Use Excel value for all mismatches shown
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    onClick={() => setReconciliationSelected({})}
                    disabled={!selectedCount}
                  >
                    Clear
                  </button>
                </div>
              </div>

              <div className="mis-table-wrap" style={{ maxHeight: "50vh" }}>
                <table className="mis-table">
                  <thead>
                    <tr>
                      <th>Excel row</th>
                      <th>Date</th>
                      <th>Vehicle</th>
                      <th>Status</th>
                      <th style={{ minWidth: 460 }}>Field comparison</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleReconRows.map((row, index) => (
                      <tr
                        key={`${row.excelRow ?? "mis"}-${row.recordId ?? index}`}
                      >
                        <td className="num">{row.excelRow ?? "—"}</td>
                        <td style={{ whiteSpace: "nowrap" }}>
                          {row.date || "—"}
                        </td>
                        <td className="fw-semibold">{row.vehicle || "—"}</td>
                        <td>
                          <span
                            className={`mis-badge ${reconciliationBadge(row.status)}`}
                          >
                            {reconciliationStatusText[row.status] || row.status}
                          </span>
                          {row.message && (
                            <div className="small text-muted mt-1">
                              {row.message}
                            </div>
                          )}
                        </td>
                        <td>
                          {row.fields?.length ? (
                            <div className="mis-recon-fields">
                              {row.fields.map((field) => {
                                const canApply =
                                  field.status === "mismatch" &&
                                  row.misId &&
                                  row.recordId;
                                return (
                                  <div
                                    className={`mis-recon-field ${field.status === "mismatch" ? "is-mismatch" : ""}`}
                                    key={field.key}
                                  >
                                    <span className="lbl">
                                      {field.label ||
                                        RECONCILIATION_FIELD_LABELS[
                                          field.key
                                        ] ||
                                        field.key}
                                    </span>
                                    <span>
                                      <span className="muted">Excel </span>
                                      <strong>
                                        {showReconciliationValue(
                                          field.excelValue,
                                        )}
                                      </strong>
                                    </span>
                                    <span>
                                      <span className="muted">MIS </span>
                                      <strong>
                                        {showReconciliationValue(
                                          field.misValue,
                                        )}
                                      </strong>
                                    </span>
                                    <span
                                      className={`mis-badge ${reconciliationBadge(field.status)}`}
                                    >
                                      {reconciliationStatusText[field.status] ||
                                        field.status}
                                    </span>
                                    {canApply ? (
                                      <label className="small d-flex align-items-center gap-1 mb-0">
                                        <input
                                          type="checkbox"
                                          className="form-check-input m-0"
                                          checked={Boolean(
                                            reconciliationSelected[
                                              reconciliationChangeKey(
                                                row,
                                                field,
                                              )
                                            ],
                                          )}
                                          onChange={(event) =>
                                            toggleReconciliationField(
                                              row,
                                              field,
                                              event.target.checked,
                                            )
                                          }
                                        />
                                        Use Excel value
                                      </label>
                                    ) : (
                                      <span />
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          ) : row.status === "missing-in-mis" ? (
                            <span className="text-muted small">
                              No matching MIS row. Nothing is inserted
                              automatically.
                            </span>
                          ) : row.status === "duplicate" ? (
                            <span className="text-muted small">
                              Resolve the duplicate MIS records before applying
                              changes.
                            </span>
                          ) : (
                            <span className="text-muted small">
                              This record exists in MIS but not in the Excel.
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                    {!visibleReconRows.length && (
                      <tr>
                        <td colSpan="5">
                          <div className="mis-empty">
                            <strong>Nothing to show here</strong>
                            Try another filter above.
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {reconciliationError && (
                <div className="mis-notice mis-notice--error mt-3" role="alert">
                  <span>{reconciliationError}</span>
                </div>
              )}
            </div>

            <div className="mis-dialog-foot">
              <span className="small text-muted">
                {selectedCount} field change{selectedCount === 1 ? "" : "s"}{" "}
                selected
              </span>
              <div className="mis-dialog-actions">
                <button
                  className="btn btn-outline-secondary"
                  onClick={closeReconciliation}
                  type="button"
                  disabled={reconciliationApplying}
                >
                  Close
                </button>
                <button
                  className="btn btn-primary"
                  onClick={applyReconciliationSelected}
                  disabled={!selectedCount || reconciliationApplying}
                  type="button"
                >
                  {reconciliationApplying
                    ? "Applying…"
                    : selectedCount
                      ? `Apply ${selectedCount} change${selectedCount === 1 ? "" : "s"}`
                      : "Apply changes"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Overlays render in <body> so the app sidebar/header can never cover them */}
      {inboxPurpose && (
        <MISInvoiceInbox
          initialPurpose={inboxPurpose}
          lockedPurpose={inboxPurpose}
          onClose={() => setInboxPurpose(null)}
        />
      )}
      {showSendMIS && clientId && (
        <MISSendEmailModal
          clientId={clientId}
          clientName={clientName}
          month={month}
          onClose={() => setShowSendMIS(false)}
          onSent={async () => {
            setShowSendMIS(false);
            setSuccessMessage("MIS email sent successfully.");
            await fetchClientDetails();
          }}
        />
      )}
      {showSendInvoice && clientId && (
        <InvoiceSendEmailModal
          clientId={clientId}
          clientName={clientName}
          month={month}
          onClose={() => setShowSendInvoice(false)}
          onSent={async () => {
            setShowSendInvoice(false);
            setSuccessMessage("Invoice email sent successfully.");
            await fetchClientDetails();
          }}
        />
      )}
      {createPortal(
        <>
          {/* Invoice PDF review */}
          {invoicePreview && (
            <div
              className="mis-backdrop mis-page mis-backdrop--top"
              role="presentation"
            >
              <div
                className="mis-dialog mis-dialog--invoice"
                role="dialog"
                aria-modal="true"
                aria-labelledby="mis-invoice-title"
              >
                <div className="mis-dialog-head">
                  <div>
                    <h5 className="mb-1" id="mis-invoice-title">
                      Review invoice PDF · {invoicePreview.clientLabel}
                    </h5>
                    <div className="small text-muted">
                      Edits change this PDF only. Saved invoice and MIS amounts
                      stay as they are.
                    </div>
                  </div>
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={closeInvoicePreview}
                    disabled={invoicePdfLoading}
                  >
                    Close
                  </button>
                </div>

                <div className="mis-invoice-body">
                  <section className="mis-invoice-fields">
                    {INVOICE_EDIT_FIELDS.map((f) => (
                      <div key={f.key}>
                        <label className="form-label" htmlFor={`inv-${f.key}`}>
                          {f.label}
                        </label>
                        {f.rows ? (
                          <textarea
                            id={`inv-${f.key}`}
                            className="form-control"
                            rows={f.rows}
                            value={invoicePdfEdits[f.key] ?? ""}
                            placeholder={f.placeholder}
                            onChange={(e) =>
                              updateInvoicePdfEdit(f.key, e.target.value)
                            }
                          />
                        ) : (
                          <input
                            id={`inv-${f.key}`}
                            className="form-control"
                            value={invoicePdfEdits[f.key] ?? ""}
                            placeholder={f.placeholder}
                            onChange={(e) =>
                              updateInvoicePdfEdit(f.key, e.target.value)
                            }
                          />
                        )}
                      </div>
                    ))}

                    <div>
                      <label className="form-label" htmlFor="inv-signature">
                        Authorised signature
                      </label>
                      {invoicePdfEdits.signatureImage && (
                        <div className="mis-signature">
                          <img
                            src={invoicePdfEdits.signatureImage}
                            alt="Signature preview"
                          />
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            onClick={handleRemoveSignature}
                            disabled={invoicePdfLoading}
                          >
                            Remove signature
                          </button>
                        </div>
                      )}
                      <input
                        id="inv-signature"
                        type="file"
                        className="form-control"
                        accept="image/png,image/jpeg"
                        onChange={handleSignatureUpload}
                        disabled={invoicePdfLoading}
                      />
                      <div className="mis-upload-hint">
                        A PNG with a transparent background works best. It's
                        remembered on this browser.
                      </div>
                    </div>

                    {invoicePdfError && (
                      <div
                        className="mis-notice mis-notice--error"
                        role="alert"
                      >
                        <span>{invoicePdfError}</span>
                      </div>
                    )}
                  </section>

                  <section
                    className="mis-invoice-doc"
                    aria-busy={invoicePdfLoading}
                  >
                    {invoicePdfUrl ? (
                      <iframe title="Invoice PDF preview" src={invoicePdfUrl} />
                    ) : (
                      !invoicePdfLoading && (
                        <div className="mis-empty">
                          <strong>No preview yet</strong>
                          Use "Update preview" to build the PDF.
                        </div>
                      )
                    )}
                    {invoicePdfLoading && (
                      <div className="mis-invoice-loading">Preparing PDF…</div>
                    )}
                  </section>
                </div>

                <div className="mis-dialog-foot">
                  <span className="small text-muted">
                    {invoiceDirty
                      ? "You have changes that aren't in the preview yet."
                      : "The preview matches your current edits."}
                  </span>
                  <div className="mis-dialog-actions">
                    <button
                      type="button"
                      className={`btn ${invoiceDirty ? "btn-primary" : "btn-outline-primary"}`}
                      onClick={handleRefreshInvoicePreview}
                      disabled={invoicePdfLoading}
                    >
                      {invoicePdfLoading ? "Updating…" : "Update preview"}
                    </button>
                    <button
                      type="button"
                      className="btn btn-success"
                      onClick={handleDownloadFinalInvoice}
                      disabled={invoicePdfLoading}
                    >
                      Download final PDF
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Invoice number confirmation: generation does not start until confirmed. */}
          {showInvoiceNumberModal && (
            <div
              className="mis-backdrop mis-page"
              style={{
                zIndex: 100030,
                background: "rgba(20,22,40,.55)",
                minHeight: 0,
                padding: 16,
              }}
              role="presentation"
              onMouseDown={(e) => {
                if (
                  e.target === e.currentTarget &&
                  !invoiceNumberLoading &&
                  !generatingInvoiceFor
                ) {
                  setShowInvoiceNumberModal(false);
                }
              }}
            >
              <div
                className="mis-dialog mis-dialog--sm"
                role="dialog"
                aria-modal="true"
                aria-labelledby="invoice-number-title"
              >
                <div className="mis-dialog-head">
                  <div>
                    <h5 id="invoice-number-title" className="mb-1">
                      Confirm invoice number
                    </h5>
                    <p className="small text-muted mb-0">
                      {pendingInvoiceClient?.clientName ||
                        getClientName(pendingInvoiceClient?.clientId)}{" "}
                      · {pendingInvoiceClient?.month || month}
                    </p>
                  </div>
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Close"
                    disabled={
                      invoiceNumberLoading || Boolean(generatingInvoiceFor)
                    }
                    onClick={() => setShowInvoiceNumberModal(false)}
                  />
                </div>
                <div className="mis-dialog-body">
                  <label className="form-label" htmlFor="mis-invoice-number">
                    Invoice number
                  </label>
                  <input
                    id="mis-invoice-number"
                    className="form-control"
                    value={invoiceNumber}
                    maxLength={80}
                    disabled={
                      invoiceNumberLoading || Boolean(generatingInvoiceFor)
                    }
                    onChange={(e) => {
                      setInvoiceNumber(e.target.value);
                      setInvoiceNumberError("");
                    }}
                    placeholder={
                      invoiceNumberLoading
                        ? "Loading suggestion…"
                        : "Enter invoice number"
                    }
                    autoFocus
                  />
                  <div className="small text-muted mt-2">
                    A suggested number is filled in automatically. You can edit
                    it before generating.
                  </div>
                  {invoiceNumberLoading && (
                    <div className="small text-muted mt-2">
                      Loading suggested invoice number…
                    </div>
                  )}
                  {invoiceNumberError && (
                    <div
                      className="mis-notice mis-notice--error mt-3"
                      role="alert"
                    >
                      {invoiceNumberError}
                    </div>
                  )}
                </div>
                <div className="mis-dialog-foot">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    disabled={Boolean(generatingInvoiceFor)}
                    onClick={() => setShowInvoiceNumberModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-success"
                    disabled={
                      invoiceNumberLoading ||
                      !invoiceNumber.trim() ||
                      !pendingInvoiceClient ||
                      Boolean(generatingInvoiceFor)
                    }
                    onClick={async () => {
                      const client = pendingInvoiceClient;
                      const confirmedNumber = invoiceNumber.trim();
                      if (!confirmedNumber || !client) return;
                      setShowInvoiceNumberModal(false);
                      await handleGenerateInvoice(client, confirmedNumber);
                    }}
                  >
                    Confirm & Generate Invoice
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Confirm dialog */}
          {confirmState && (
            <div
              className="mis-backdrop mis-page"
              style={{
                zIndex: 100020,
                background: "rgba(20,22,40,.55)",
                minHeight: 0,
                padding: 16,
              }}
              role="presentation"
              onMouseDown={(e) =>
                e.target === e.currentTarget && closeConfirm(false)
              }
            >
              <div
                className="mis-dialog mis-dialog--sm"
                role="alertdialog"
                aria-modal="true"
                aria-labelledby="mis-confirm-title"
              >
                <h5 id="mis-confirm-title">{confirmState.title}</h5>
                <p>{confirmState.message}</p>
                <div className="mis-dialog-actions">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => closeConfirm(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    autoFocus
                    className={`btn ${confirmState.danger ? "btn-danger" : "btn-primary"}`}
                    onClick={() => closeConfirm(true)}
                  >
                    {confirmState.confirmLabel || "Confirm"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </>,
        document.body,
      )}
    </>
  );
}
